// status-progress-facts-reader.ts
//
// Progress panel P1 (R4/R5): `readStatusProgress` — the ONE function both
// `status --json` and `status --watch --json` call (command layer, B6) to
// build `payload.progress`. All I/O lives here, every read lock-free and
// best-effort: ONE journal scan shared by every reader that takes records
// (journey facts, recovery ledger, stop attempts/escalations, decision ledger
// — N7), the supervisor's liveness read from `machineLockPath` here (N14, ONE read of the lock),
// never handed in. Derivations are the pure modules next to this one.
import { readJournalRecordsLockFree } from "./attempt-finished-journal-reader.js";
import { projectDecisionLedger } from "./decision-ledger-projection.js";
import { escalationsFromRecords } from "./escalation-trigger-from-journal.js";
import { readGateFile } from "./gate-registry.js";
import { readInterview } from "./interview-store.js";
import { redactKnownValuesDeep } from "./known-secret-values-deep-display-redaction.js";
import { replayRecords } from "./mission-projection-io.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import { emptyMissionState } from "./mission-state-reducer.js";
import { readPauseResumeState } from "./pause-resume-state.js";
import { type LockLivenessProbe, realLivenessProbe } from "./process-liveness-probe.js";
import {
  buildCleanReviewCandidateTicket,
  buildReviewCandidateTicket,
} from "./review-candidate-session.js";
import { readMaxCountedFailures } from "./status-effective-config-readers.js";
import { buildTicketOperatingRows } from "./status-operating-table.js";
import {
  deriveCurrentAttempt,
  deriveHumanActions,
  supervisorDiedMidMission,
} from "./status-progress-current-attempt-and-human-actions.js";
import { progressJournalFacts } from "./status-progress-journal-facts.js";
import {
  activeStageOf,
  deriveJourney,
  isMissionTerminalFromStatusLabel,
} from "./status-progress-journey-derivation.js";
import { liveRecoveries } from "./status-progress-journey-steps.js";
import { isStatusLabelStale } from "./status-progress-status-label-classes.js";
import {
  confirmedRecoveries,
  loadTickets,
  readLiveWorkerPid,
  readReleaseConfigured,
} from "./status-progress-ticket-release-and-worker-readers.js";
import { buildProgressTickets } from "./status-progress-ticket-rows.js";
import { PROGRESS_SCHEMA_VERSION, type StatusProgress } from "./status-progress-types.js";
import { readMachineLockFacts } from "./status-supervisor-liveness.js";
import { readAttemptStateFile } from "./supervisor-attempt-state.js";

export interface ReadStatusProgressInput {
  controlRoot: string;
  /** `<mission_home>/workspace` — where `.avatar/tickets/*.json` live. */
  missionWorkspace: string;
  missionId: string;
  machineLockPath: string;
  now?: () => Date;
  /** Test seam for supervisor/worker pid liveness. */
  probe?: LockLivenessProbe;
}

export async function readStatusProgress(input: ReadStatusProgressInput): Promise<StatusProgress> {
  const { controlRoot, missionWorkspace, missionId } = input;
  const probe = input.probe ?? realLivenessProbe;
  const [records, loaded, attemptState, gates, maxCounted, interview, paused, lockFacts, release] =
    await Promise.all([
      readJournalRecordsLockFree(controlRoot),
      loadTickets(missionWorkspace),
      readAttemptStateFile(controlRoot),
      readGateFile(controlRoot),
      readMaxCountedFailures(controlRoot),
      readInterview(controlRoot).catch(() => undefined),
      readPauseResumeState(controlRoot).then(
        (s) => s.paused === true,
        () => false,
      ),
      readMachineLockFacts(input.machineLockPath, missionId, probe),
      readReleaseConfigured(controlRoot),
    ]);
  const { liveness } = lockFacts;
  const state = replayRecords(emptyMissionState(), records, 1);
  const facts = progressJournalFacts(records);
  const { tickets } = loaded;
  const byId = new Map(tickets.map((t) => [t.id, t]));
  const recoveries = confirmedRecoveries(records, state.tickets, byId);
  // DF-P4-1: review candidates have no ticket file — a row once the journal has seen them.
  const reserved = [buildReviewCandidateTicket(), buildCleanReviewCandidateTicket()];
  const rowTickets = [
    ...tickets,
    ...reserved.filter((t) => state.tickets[t.id] !== undefined && !byId.has(t.id)),
  ];
  const rows = buildTicketOperatingRows(
    rowTickets,
    state.tickets,
    attemptState,
    state.ticket_last_verdict,
    maxCounted,
    interview,
  );
  const progressTickets = buildProgressTickets({
    tickets: rowTickets,
    rows,
    facts,
    recoveryOf: new Map(recoveries.map((r) => [r.recovery_ticket, r.source_ticket])),
  });
  const statusLabel = paused ? "paused" : state.status;
  const anyRunning = Object.values(state.tickets).includes("in_progress");
  const labelStale = isStatusLabelStale({
    label: statusLabel,
    lastStatusLabelSeq: facts.lastStatusLabelSeq,
    lastWorkSeq: facts.lastWorkSeq,
    supervisorAlive: liveness.supervisor_alive,
    anyRunning,
  });
  const journeyInput = {
    gates,
    tickets: progressTickets,
    journalTicketStatus: state.tickets,
    recoveries,
    ...(statusLabel === undefined ? {} : { statusLabel }),
    labelStale,
    releaseConfigured: release,
  };
  const journey = deriveJourney(journeyInput);
  const executorOf = new Map([...reserved, ...tickets].map((t) => [t.id, t.executor]));
  const liveWorkerPid =
    liveness.supervisor_alive && anyRunning
      ? await readLiveWorkerPid(controlRoot, probe)
      : undefined;
  const currentAttempt = deriveCurrentAttempt({
    facts,
    ticketStatus: state.tickets,
    executorOf,
    supervisorAlive: liveness.supervisor_alive,
    ...(liveWorkerPid === undefined ? {} : { liveWorkerPid }),
  });
  const terminal = isMissionTerminalFromStatusLabel(state.status, release);
  const orphanedLock = !liveness.supervisor_alive && lockFacts.ownLockPresent;
  const humanActions = deriveHumanActions({
    missionId,
    gates,
    pendingDecisions: projectDecisionLedger(
      records.map((r) => r.delta),
      interview,
    ).pending,
    ...(statusLabel === undefined ? {} : { statusLabel }),
    labelStale,
    escalations: await escalationsFromRecords(records, missionWorkspace, { standing: true }),
    recoveringSources: new Set(liveRecoveries(journeyInput).map((r) => r.source_ticket)),
    // "Máy đang bận" only for a LIVE foreign lock; a stale one is just a dead supervisor.
    ...(lockFacts.otherLockAlive && liveness.machine_lock_mission_id !== undefined
      ? { machineBusyWith: liveness.machine_lock_mission_id }
      : {}),
    supervisorDead: supervisorDiedMidMission({
      supervisorAlive: liveness.supervisor_alive,
      terminal,
      paused,
      orphanedLock,
      ticketStatus: state.tickets,
    }),
  });
  const activeStage = activeStageOf(journey);
  const progress: StatusProgress = {
    schema_version: PROGRESS_SCHEMA_VERSION,
    generated_at: (input.now ?? (() => new Date()))().toISOString(),
    mission: {
      id: missionId,
      ...(facts.missionStartedAt === undefined ? {} : { started_at: facts.missionStartedAt }),
      ...(statusLabel === undefined ? {} : { status_label: statusLabel }),
      ...(labelStale ? { status_label_stale: true } : {}),
      supervisor_alive: liveness.supervisor_alive,
      terminal,
      ...(activeStage === undefined ? {} : { active_stage: activeStage }),
    },
    journey,
    tickets: progressTickets,
    ...(loaded.error === undefined ? {} : { tickets_error: loaded.error }),
    ...(currentAttempt === undefined ? {} : { current_attempt: currentAttempt }),
    human_actions: humanActions,
  };
  // Same sink rule as readStatusSnapshot (Phase 04a): mask the mission's known values.
  return redactKnownValuesDeep(progress, await knownValuesForControlRoot(controlRoot));
}
